import { Check } from "@frui.ts/bootstrap";
import { registerView, View, ViewComponent } from "@frui.ts/views";
import EdazPhoneInput from "@edaz/edazkit/InputPhone";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Col, Form } from "react-bootstrap";
import ContactViewModel from "viewModels/common/contactViewModel";
import EmailInput from "views/common/emailInput/emailInput";
import { DataTestIds } from "data-test-constants";
import ChargeManagementContactViewModel from "viewModels/chargeManagement/chargeManagementEdit/chargeManagementContactViewModel";
import AlertWithInfoIcon from "../alertWithInfoIcon/alertWithInfoIcon";
import ReceiptEmailModalViewModel from "viewModels/chargeManagement/chargeManagementEdit/receiptEmailModalViewModel";

const ContactView: ViewComponent<ContactViewModel | ChargeManagementContactViewModel | ReceiptEmailModalViewModel> = observer(
  ({ vm }) => {
    const ta = (code: string) => {
      return vm.localizationService.translateAttribute("contact", code);
    };

    const translateContact = (translateKey?: string) => (code: string) =>
      vm.localizationService.translateAttribute(translateKey ? `contact.${translateKey}` : "contact", code);
    const translateChargeManagmentContact = (translateKey?: string) => (code: string) =>
      vm.localizationService.translateAttribute(
        translateKey ? `contact.chargeManagement.${translateKey}` : `contact.chargeManagement`,
        code
      );
    const isContactViewModel = vm instanceof ContactViewModel;
    return (
      <fieldset>
        {isContactViewModel && !vm.isSingleEshop && !vm.isMultiEshop && (
          <legend className="mt-4 h3">{vm.translate("title")}</legend>
        )}
        <Form.Row>
          {isContactViewModel && vm.isSingleEshop && (
            <Form.Group as={Col} xs="auto">
              <p>{vm.translate("emailTooltip")}</p>
            </Form.Group>
          )}
          {isContactViewModel && vm.isMultiEshop && (
            <Form.Group as={Col} xs="auto">
              <p>{vm.translateMulti("emailTooltip")}</p>
            </Form.Group>
          )}
          <EmailInput
            id={DataTestIds.EMAIL_INPUT}
            xl={12}
            translate={isContactViewModel ? translateContact() : translateChargeManagmentContact()}
            target={vm.notificationPreferences}
            property="emailAddress"
            disablePaste={true}
            className="mb-6"
          />
          <EmailInput
            id={DataTestIds.EMAIL_CONFIRMATION_INPUT}
            xl={12}
            translate={isContactViewModel ? translateContact("again") : translateChargeManagmentContact("again")}
            target={vm.notificationPreferences}
            property="_emailConfirmation"
            disablePaste={true}
            className="mb-6"
          />
        </Form.Row>
        {isContactViewModel && !vm.isSingleEshop && !vm.isMultiEshop && (
          <Form.Row>
            <Form.Group as={Col} xs="auto">
              <p>{vm.translate("emailTooltip")}</p>
            </Form.Group>
          </Form.Row>
        )}
        {vm.showNotificationPreferences && (
          <Form.Row>
            <Form.Group as={Col} className="mb-4">
              <Check
                autoComplete="off"
                custom
                target={vm}
                property="notificationEnabled"
                label={
                  isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop) ? (
                    <span className="font-weight-normal">
                      <span>{vm.translateNotification("title")}</span>
                      {!vm.isMultiEshop && (
                        <>
                          <br />
                          <span className="text-muted font-size-sm">{vm.translateNotification("subtitle")}</span>
                        </>
                      )}
                    </span>
                  ) : (
                    ta("notificationEnabled")
                  )
                }
              />
            </Form.Group>
          </Form.Row>
        )}
        {vm.notificationEnabled && (
          <div>
            {vm.notificationEnabledInfo && (
              <Form.Row>
                <Form.Group as={Col} xs="auto" sm={12} xl={12} className="mr-7 mb-4">
                  <AlertWithInfoIcon
                    variant={isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop) ? "info-light" : "warning"}>
                    <span className={isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop) ? "font-weight-normal" : ""}>
                      {vm.notificationEnabledInfo}
                    </span>
                  </AlertWithInfoIcon>
                </Form.Group>
              </Form.Row>
            )}
            {isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop) && (
              <Form.Row>
                <Form.Group as={Col} className="mb-6">
                  <Check
                    autoComplete="off"
                    custom
                    disabled={!vm.notificationPreferences.emailAddress}
                    target={vm.notificationPreferences}
                    property="_isNotificationEmailSame"
                    label={ta("notificationSameEmail")}
                  />
                </Form.Group>
              </Form.Row>
            )}
            <Form.Row className="mt-0">
              <EmailInput
                id={DataTestIds.EMAIL_NOTIFICATION_INPUT}
                xl={12}
                translate={translateContact(
                  isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop) ? "optionalEmail" : "classicOptionalEmail"
                )}
                disablePaste={true}
                target={vm.notificationPreferences}
                disabled={vm.isNotificationEmailSame}
                property="_emailAddressNotification"
                className="mb-6"
                onBlur={vm.onEmailAddressNotificationChanged}
              />
            </Form.Row>

            <Form.Row className="mt-0">
              <Form.Group as={Col} sm={6} xl={6}>
                <Form.Label>
                  {ta(
                    isContactViewModel && (vm.isSingleEshop || vm.isMultiEshop)
                      ? "optionalPhoneNumber"
                      : "classicOptionalPhoneNumber"
                  )}
                </Form.Label>
                <EdazPhoneInput
                  target={vm.notificationPreferences}
                  property="phoneNumber"
                  defaultPrefix={vm.defaultPrefixNumber}
                  placeholder={ta("phoneNumberPlaceholder")}
                  onBlur={vm.onPhoneNumberChanged}
                />
              </Form.Group>
            </Form.Row>
          </div>
        )}
        {isContactViewModel && !vm.isSingleEshop && !vm.isMultiEshop && <View vm={vm.gdprViewModel} />}
      </fieldset>
    );
  }
);

registerView(ContactView, ContactViewModel);
registerView(ContactView, ChargeManagementContactViewModel);
